import React from 'react';

import { toCssSelector } from '@candu-ai/stylesheet/utils';
import { formatCypressProps } from 'commons/utils';
import Node from 'craft/components/Node';
import { createVariantsSelector } from 'craft/components/SelectedToolbar/VariantsSelector';
import { AtomicText } from 'craft/resolvers/AtomicText';
import { TypographyStyles } from 'craft/resolvers/Typography/toolbox';
import { Resolver, ToolboxComponentSections } from 'craft/types';

import { ButtonBadgesVariantSelectorToolbox } from './ButtonBadgesVariantSelectorToolbox';

interface Props {
  color?: string;
  childrenString: string;
  size?: string;
  outline?: string;
  variant?: string;
}

export const Button: Resolver<Props> = ({
  color,
  variant,
  outline,
  childrenString,
  size,
}: Props) => {
  const colorMatchedVariant = color === 'primary' ? 'base' : color;
  const appearanceStyle = outline === 'light' ? '_light' : '';
  const buttonVariant = {
    variant: variant || `${colorMatchedVariant}${appearanceStyle}` || 'base',
  };
  const cssClassName = toCssSelector('button', {
    variant: buttonVariant,
    output: 'html',
  });

  return (
    <Node
      data-atomictext-node
      is="div"
      color={color}
      size={size}
      light={outline === 'light'}
      data-cy-props={formatCypressProps({ outline, size, color })}
      className={cssClassName}
      css={{ display: 'inline-block', wordBreak: 'keep-all' }}
    >
      <AtomicText data-cy="node-button-title" childrenString={childrenString} />
    </Node>
  );
};

Button.craft = {
  name: 'Button',
  defaultProps: {
    outline: 'solid',
    size: 'md',
    color: 'primary',
  },
};

Button.toolbox = {
  component: [TypographyStyles],
  schema: {
    [ToolboxComponentSections.NameAndType]: {
      variantSelector: ButtonBadgesVariantSelectorToolbox,
    },
    [ToolboxComponentSections.Layout]: {
      blockedDisplays: ['flex'],
      defaultDisplay: 'inline-block',
    },
    [ToolboxComponentSections.Size]: {
      hasOverflow: true,
    },
    [ToolboxComponentSections.Background]: {
      hasDefaultValue: true,
    },
  },
};

Button.selectedToolbar = {
  customItems: [
    createVariantsSelector({
      componentName: 'button',
      getConditions: (props) => {
        const colorMatchedVariant = props.color?.value === 'primary' ? 'base' : props.color?.value;
        const appearanceStyle = props.outline?.value === 'light' ? '_light' : '';
        return {
          color: props.color?.value,
          outline: props.outline?.value,
          name: props.variant?.value || `${colorMatchedVariant}${appearanceStyle}` || 'base',
        };
      },
      variantToProps: (variant) => variant.conditions,
    }),
  ],
};
